import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Col, Row, Button } from "react-bootstrap";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { useVigilanteReloadContext } from "../../../../context/VisitantesVigilantesContext";
import CorrespondenciaVigilanteService from "../../../../services/CorrespondenciaVigilanteService";
import { InmuebleIcon } from "../components/Icons/InmuebleIcon";
import { ImageWithFallback } from "../components/ImageWithFallback";
import { SpinnerCitoPlus } from "../components/loadingLogo";

const CorrespondenciaHistorialCard = ({ item }) => {
  const handleImageClick = (index) => {
    setActiveIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <Card>
        <Card.Body>
          <h4 className="fs-20">{item.vigilante_nombre_ingreso}</h4>
          <div className="listline-wrapper mb-4">
            <span className="item">
              <InmuebleIcon />
              {item.inmueble && <span className="ms-1">{item.inmueble}</span>}
              <i className="fas fa-caret-right ms-2 me-2"></i>
              {item.created_at}
            </span>
          </div>
          <div className="d-sm-flex align-items-start pe-md-3 pe-0">
            <div className="d-flex gap-2 flex-shrink-0">
              <ImageWithFallback
                src={item.foto}
                alt="Foto"
                className="img-thumbnail"
                style={{
                  objectFit: "cover",
                  width: "80px",
                  height: "80px",
                  cursor: "pointer",
                }}
                onClick={() => handleImageClick(0)}
              />
              {item.recibido_firma && (
                <ImageWithFallback
                  src={item.recibido_firma}
                  alt="Firma recibida"
                  className="img-thumbnail"
                  style={{
                    objectFit: "cover",
                    width: "80px",
                    height: "80px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleImageClick(1)}
                />
              )}
            </div>
            <div className="ms-sm-3 mt-2 mt-sm-0">
              <p className="mb-1">{item.obj}</p>
              {item.nota_salida && (
                <p className="mb-0 text-muted">
                  Nota Salida: {item.nota_salida}
                </p>
              )}
            </div>
          </div>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap">
          <div className="mb-2">
            <span className="text-muted">
              Vigilante: {item.vigilante_nombre_salida}
            </span>
            {item.recibido_nombre && (
              <p className="mb-0 text-black font-w500 mt-1">
                Recogido: {item.recibido_nombre}
              </p>
            )}
          </div>
          <div className="mb-2">
            <span className="text-muted">Recogido el {item.updated_at}</span>
          </div>
        </Card.Footer>
      </Card>
    </>
  );
};

export const CorrespondenciaHistorial = () => {
  const { reloadData, setReloadData } = useVigilanteReloadContext();
  const [historial, setHistorial] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [filterDate, setFilterDate] = useState({
    date_after: "",
    date_before: "",
  });

  const fetchHistorial = useCallback(
    async (page = 1, append = false) => {
      setLoadingHistorial(true);
      try {
        const response = await CorrespondenciaVigilanteService.getHistorial({
          page,
          search: filterText,
          ...filterDate,
        });
        if (append) {
          setHistorial((prevHistorial) => [
            ...prevHistorial,
            ...response.data.results,
          ]);
        } else {
          setHistorial(response.data.results);
        }
        setHasNextPage(!!response.data.next);
        setCurrentPage(page);
      } catch (error) {
        console.error("Error fetching historial:", error);
      } finally {
        setLoadingHistorial(false);
      }
    },
    [filterText, filterDate],
  );

  const handleSearch = useCallback(
    (event) => {
      event.preventDefault();
      fetchHistorial(1);
    },
    [fetchHistorial],
  );

  const clearFilters = useCallback(() => {
    setFilterText("");
    setFilterDate({
      date_after: "",
      date_before: "",
    });
    fetchHistorial(1);
  }, [fetchHistorial]);

  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || loadingHistorial) return;
    fetchHistorial(currentPage + 1, true);
  }, [hasNextPage, loadingHistorial, fetchHistorial, currentPage]);

  useEffect(() => {
    fetchHistorial(1);
  }, []);

  useEffect(() => {
    if (reloadData.correspondencia) {
      fetchHistorial(1);
      setReloadData((prev) => ({ ...prev, correspondencia: false }));
    }
  }, [reloadData.correspondencia, fetchHistorial, setReloadData]);

  const isResetDisabled =
    !filterText && !filterDate.date_after && !filterDate.date_before;

  return (
    <>
      <section className="card">
        <header className="card-header">
          <div className="d-flex flex-wrap align-items-center gap-2 w-100">
            <div className="d-flex flex-column me-auto flex-grow-1">
              <h2 className="card-title mb-0">Correspondencia - Historial</h2>
              <div className="text-muted small">
                Historial de correspondencia entregada
              </div>
            </div>
            <div className="d-flex gap-2 ms-auto">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm text-body"
                onClick={clearFilters}
                disabled={loadingHistorial || isResetDisabled}
              >
                <i className="fas fa-times me-2"></i>
                Limpiar filtros
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm text-white"
                onClick={handleSearch}
                disabled={loadingHistorial}
              >
                {loadingHistorial ? (
                  <span className="d-inline-flex align-items-center gap-2">
                    <i
                      className="fas fa-circle-notch fa-spin"
                      aria-hidden="true"
                    />
                    <span>Consultando...</span>
                  </span>
                ) : (
                  <>
                    <i className="fas fa-search me-2" />
                    Consultar
                  </>
                )}
              </button>
            </div>
          </div>
        </header>

        <div className="card-body">
          <div className="row g-3 mb-4">
            <div className="col-xl-4 col-lg-5 col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por texto"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch(e);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {loadingHistorial && historial.length === 0 ? (
        <div className="py-5 text-center">
          <SpinnerCitoPlus text="Cargando historial de correspondencia..." />
        </div>
      ) : historial.length === 0 ? (
        <div className="alert alert-light border" role="alert">
          No encontramos correspondencia para los criterios seleccionados.
        </div>
      ) : (
        <>
          <Row>
            {historial.map((item) => (
              <Col xl={6} key={item.slug}>
                <CorrespondenciaHistorialCard item={item} />
              </Col>
            ))}
          </Row>
          {hasNextPage && (
            <div className="d-flex justify-content-center mt-3">
              <Button onClick={handleLoadMore} disabled={loadingHistorial}>
                {loadingHistorial ? "Cargando..." : "Cargar más"}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CorrespondenciaHistorial;
